import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Film } from 'lucide-react';

import AppRoutes from './routes/AppRoutes';
import { CatalogProvider } from './providers/CatalogProvider';
import { NotificationProvider, useNotification } from './components/NotificationContext';
import NotificationContainer from './components/NotificationContainer';
import ErrorBoundary from './components/ErrorBoundary';

import TitleSheet from './components/title/TitleSheet';
import SeriesView from './SeriesView';
import SettingsModal from './SettingsModal';
import MovieRequestForm from './MovieRequestForm';
import MaintenanceBanner from './MaintenanceBanner';
import ChangelogModal from './ChangelogModal';
import ChatWidget from './ChatWidget';
import MyPlaylists from './components/MyPlaylists';
import WatchPartyTheater from './components/WatchPartyTheater';
import AchievementNotification from './components/AchievementNotification';
import ScrollToTop from './components/ScrollToTop';
import PullToRefresh from './PullToRefresh';

import useAchievements from './hooks/useAchievements';
import usePresencePing from './hooks/usePresencePing';
import apiService from './services/apiService';
import { API_CONFIG } from './config/apiConfig';

/* Cadences de sondage — voir `usePoll` plus bas. */
const MAINTENANCE_MS = 30000;
const NOTIFICATIONS_MS = 20000;
const ACCOUNT_MS = 120000;

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'X-Admin-Token': localStorage.getItem('cinestream_token') || '',
});

/**
 * Sondage périodique qui se met en veille quand l'onglet n'est pas visible.
 *
 * L'application interrogeait le serveur toutes les 10 s pour la maintenance,
 * 10 s pour les notifications et 30 s pour la validité du compte — sans
 * jamais s'arrêter, y compris onglet en arrière-plan ou téléphone en poche.
 * Cela représentait plus de 700 requêtes par heure et par session ouverte.
 */
function usePoll(task, delay, enabled = true) {
  const saved = useRef(task);
  saved.current = task;

  useEffect(() => {
    if (!enabled) return undefined;

    let timer = null;
    const run = () => {
      if (!document.hidden) saved.current();
      timer = setTimeout(run, delay);
    };

    saved.current();
    timer = setTimeout(run, delay);

    // Au retour dans l'onglet, on rafraîchit tout de suite plutôt que
    // d'attendre la fin du cycle en cours.
    const onVisible = () => {
      if (!document.hidden) saved.current();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [delay, enabled]);
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const notify = useNotification();

  /* ── Session ─────────────────────────────────────────────────── */
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [booting, setBooting] = useState(true);
  // Serveur injoignable au démarrage : la session est conservée, on propose
  // simplement de réessayer.
  const [sessionUnreachable, setSessionUnreachable] = useState(false);

  /* ── Catalogue ───────────────────────────────────────────────── */
  const [movies, setMovies] = useState([]);
  const [allMovies, setAllMovies] = useState([]);

  /* ── Sélection courante ──────────────────────────────────────── */
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState(null);

  /* ── Données utilisateur ─────────────────────────────────────── */
  const [watchlist, setWatchlist] = useState([]);
  const [watchedMovies, setWatchedMovies] = useState([]);
  const [watchProgress, setWatchProgress] = useState({});
  const [progressList, setProgressList] = useState([]);
  const [dislikedMovies, setDislikedMovies] = useState([]);

  /* ── Interface ───────────────────────────────────────────────── */
  const [showSettings, setShowSettings] = useState(false);
  const [showTheater, setShowTheater] = useState(false);
  const [showPlaylists, setShowPlaylists] = useState(false);
  const [showMovieRequest, setShowMovieRequest] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [isWatchingVideo, setIsWatchingVideo] = useState(false);

  /* ── Maintenance ─────────────────────────────────────────────── */
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  const { notifications, removeNotification, checkAchievements } = useAchievements(currentUser?.id);
  usePresencePing(currentUser, isWatchingVideo);

  const scrollMemory = useRef(0);

  /* ═══════════════════════ Authentification ═══════════════════════ */

  const handleLogout = useCallback(() => {
    localStorage.removeItem('cinestream_token');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setMovies([]);
    setBooting(false);
    navigate('/login');
  }, [navigate]);

  // Rétablit la session au démarrage.
  //
  // Historiquement, le moindre échec de `verifyToken` effaçait le jeton :
  // un backend en train de redémarrer, un 500, un wifi qui bascule, un
  // téléphone qui sort de veille — tout finissait en déconnexion. D'où les
  // reconnexions à répétition sur un même appareil, et la liste d'appareils
  // qui enflait, puisque chaque connexion crée une session de plus.
  //
  // On ne déconnecte donc que sur un refus explicite du serveur. Le reste
  // est passager : on réessaie, et on garde le jeton.
  const restoreSession = useCallback(async ({ retries = 2 } = {}) => {
    const token = localStorage.getItem('cinestream_token');
    if (!token) {
      setBooting(false);
      return;
    }

    setSessionUnreachable(false);

    try {
      const data = await apiService.verifyToken(token);

      if (!data.success) {
        handleLogout();
        return;
      }

      setCurrentUser(data.user);
      setIsAuthenticated(true);
      setBooting(false);
      await checkAchievements();
    } catch (error) {
      if (error?.status === 401 || error?.status === 403) {
        handleLogout();
        return;
      }

      if (retries > 0) {
        // Un redémarrage de backend dure quelques secondes : on laisse
        // passer l'orage avant de renoncer.
        await new Promise((resolve) => setTimeout(resolve, 1500));
        return restoreSession({ retries: retries - 1 });
      }

      setSessionUnreachable(true);
      setBooting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleLogout]);

  useEffect(() => {
    restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoginSuccess = (user) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
    notify.success(`Bienvenue ${user.username} ! Bon visionnage 🎬`);
  };

  /* ═══════════════════════ Chargement des données ═════════════════ */

  useEffect(() => {
    if (!isAuthenticated) return;
    apiService
      .getMovies()
      .then(setMovies)
      .catch((error) => {
        console.error('Chargement du catalogue impossible :', error);
        notify.error('Impossible de charger le catalogue.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadUserData = useCallback(async () => {
    if (!currentUser) return;

    const [listRes, watched, progress, disliked] = await Promise.allSettled([
      fetch(`${API_CONFIG.NEW_API_BASE}/watchlist?user_id=${currentUser.id}`, { headers: authHeaders() }).then((r) => r.json()),
      apiService.getWatchedMovies(currentUser.id),
      fetch(`${API_CONFIG.NEW_API_BASE}/progress/list?user_id=${currentUser.id}`, { headers: authHeaders() }).then((r) => r.json()),
      fetch(`${API_CONFIG.NEW_API_BASE}/user/disliked-movies`, { headers: authHeaders() }).then((r) => r.json()),
    ]);

    if (listRes.status === 'fulfilled' && listRes.value?.success) setWatchlist(listRes.value.movies || []);
    if (watched.status === 'fulfilled' && watched.value?.success) setWatchedMovies(watched.value.movie_ids || []);

    if (progress.status === 'fulfilled' && progress.value?.success) {
      const list = (progress.value.progress_list || []).sort(
        (a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)
      );
      setProgressList(list);
      const map = {};
      list.forEach((entry) => {
        map[String(entry.movie_id)] = entry.percentage;
      });
      setWatchProgress(map);
    }

    if (disliked.status === 'fulfilled' && disliked.value?.success) {
      // Conservé pour compatibilité : les titres écartés restent filtrables.
      setDislikedMovies((disliked.value.data || []).map((m) => String(m.id)));
    }
  }, [currentUser]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const fetchAllMoviesForAdmin = useCallback(async (timestamp = null) => {
    try {
      // L'admin est appelé juste après une modification du catalogue : il lui
      // faut toujours du frais. Le paramètre peut arriver d'un `onMoviesUpdate`
      // branché directement sur un événement, d'où le contrôle de type.
      const ts = Number.isFinite(timestamp) ? timestamp : Date.now();
      setAllMovies(await apiService.getMovies({ includeInactive: true, timestamp: ts }));
    } catch (error) {
      console.error('Chargement admin impossible :', error);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    if (!currentUser) return false;
    try {
      const timestamp = Date.now();
      setMovies(await apiService.getMovies({ timestamp }));
      if (currentUser.role === 'admin') await fetchAllMoviesForAdmin(timestamp);
      await loadUserData();
      notify.success('Données actualisées');
      return true;
    } catch (error) {
      console.error('Actualisation impossible :', error);
      notify.error("Échec de l'actualisation");
      throw error;
    }
  }, [currentUser, fetchAllMoviesForAdmin, loadUserData, notify]);

  /* ═══════════════════════ Sondages ═══════════════════════════════ */

  usePoll(
    useCallback(() => {
      fetch(`${API_CONFIG.NEW_API_BASE}/maintenance`)
        .then((r) => r.json())
        .then((data) => {
          if (!data.success) return;
          setMaintenanceEnabled(data.maintenance.enabled);
          setMaintenanceMessage(data.maintenance.message);
          // Maintenance déclenchée pendant une lecture : on met en pause.
          if (data.maintenance.enabled) {
            document.querySelectorAll('video').forEach((video) => !video.paused && video.pause());
          }
        })
        .catch(() => {});
    }, []),
    MAINTENANCE_MS
  );

  usePoll(
    useCallback(() => {
      if (!currentUser) return;
      fetch(`${API_CONFIG.NEW_API_BASE}/notifications?user_id=${currentUser.id}`, { headers: authHeaders() })
        .then((r) => r.json())
        .then((data) => {
          if (!data.success) return;
          (data.notifications || []).forEach((item) => notify[item.type]?.(item.message));
        })
        .catch(() => {});
    }, [currentUser, notify]),
    NOTIFICATIONS_MS,
    isAuthenticated && Boolean(currentUser)
  );

  usePoll(
    useCallback(() => {
      const token = localStorage.getItem('cinestream_token');
      if (!token) return;
      apiService
        .verifyToken(token)
        .then((data) => {
          if (!data.success) {
            notify.error('Votre compte a été désactivé. Vous avez été déconnecté.');
            handleLogout();
          }
        })
        .catch(() => {});
    }, [handleLogout, notify]),
    ACCOUNT_MS,
    isAuthenticated && Boolean(currentUser)
  );

  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;
    fetch(`${API_CONFIG.NEW_API_BASE}/changelogs/check?user_id=${currentUser.id}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.has_unseen) setTimeout(() => setShowChangelog(true), 2000);
      })
      .catch(() => {});
  }, [isAuthenticated, currentUser]);

  /* ═══════════════════════ Synchronisation URL ↔ sélection ════════ */

  const openTitle = useCallback((title) => {
    if (!title) return;
    if (title.isGroupedSeries) {
      setSelectedMovie(null);
      setSelectedSeries(title);
      navigate(`/serie/${title.tmdbId}`);
    } else {
      setSelectedMovie(title);
      navigate(`/film/${title.id}`);
    }
  }, [navigate]);

  const closeTitle = useCallback(() => {
    if (selectedMovie?.type === 'serie' && selectedSeries) {
      setSelectedMovie(null);
      navigate(`/serie/${selectedSeries.tmdbId}`);
      return;
    }
    setSelectedMovie(null);
    setSelectedSeries(null);
    navigate('/');
  }, [navigate, selectedMovie, selectedSeries]);

  /**
   * L'URL est la source de vérité.
   *
   * L'ancienne version faisait circuler l'information dans les deux sens
   * (trois `useEffect` qui se déclenchaient l'un l'autre), d'où les commentaires
   * « CONDITION CRITIQUE » et les allers-retours de navigation. Ici, l'URL
   * décide, l'état suit.
   */
  useEffect(() => {
    if (!movies.length) return;
    const path = location.pathname;

    if (path.startsWith('/film/')) {
      const id = path.slice('/film/'.length);
      const movie = movies.find((m) => String(m.id) === id);
      if (!movie) {
        navigate('/', { replace: true });
        return;
      }
      setSelectedMovie((current) => (String(current?.id) === id ? current : movie));

      if (movie.type === 'serie' && movie.tmdbId) {
        setSelectedSeries((current) =>
          current?.tmdbId === movie.tmdbId
            ? current
            : {
                ...movie,
                isGroupedSeries: true,
                episodes: movies.filter((m) => m.type === 'serie' && m.tmdbId === movie.tmdbId),
              }
        );
      } else {
        setSelectedSeries(null);
      }
      return;
    }

    if (path.startsWith('/serie/')) {
      const tmdbId = path.slice('/serie/'.length);
      const serie = movies.find((m) => m.type === 'serie' && String(m.tmdbId) === tmdbId);
      if (!serie) {
        navigate('/', { replace: true });
        return;
      }
      setSelectedMovie(null);
      setSelectedSeries((current) =>
        String(current?.tmdbId) === tmdbId
          ? current
          : {
              ...serie,
              isGroupedSeries: true,
              episodes: movies.filter((m) => m.type === 'serie' && String(m.tmdbId) === tmdbId),
            }
      );
      return;
    }

    setSelectedMovie(null);
    setSelectedSeries(null);
  }, [location.pathname, movies, navigate]);

  /* Mémorise la position de défilement avant d'ouvrir une fiche. */
  useEffect(() => {
    if (selectedMovie || selectedSeries) {
      scrollMemory.current = window.scrollY;
    } else if (location.pathname === '/' && scrollMemory.current) {
      const restore = requestAnimationFrame(() =>
        window.scrollTo({ top: scrollMemory.current, behavior: 'instant' })
      );
      return () => cancelAnimationFrame(restore);
    }
    return undefined;
  }, [selectedMovie, selectedSeries, location.pathname]);

  /* ═══════════════════════ Ma liste ═══════════════════════════════ */

  const toggleWatchlist = useCallback(
    async (movieId) => {
      if (!currentUser) return;

      /* Série entière : une seule requête pour tous ses épisodes. */
      if (typeof movieId === 'string' && movieId.startsWith('series_')) {
        const tmdbId = Number(movieId.slice('series_'.length));
        const episodeIds = movies
          .filter((m) => m.type === 'serie' && m.tmdbId === tmdbId && m.episode)
          .map((ep) => ep.id);

        if (!episodeIds.length) {
          notify.error('Aucun épisode trouvé pour cette série');
          return;
        }

        const allIn = episodeIds.every((id) => watchlist.includes(id));
        const rollback = watchlist;
        setWatchlist((prev) =>
          allIn
            ? prev.filter((id) => !episodeIds.includes(id))
            : [...prev, ...episodeIds.filter((id) => !prev.includes(id))]
        );

        try {
          const response = await fetch(`${API_CONFIG.NEW_API_BASE}/watchlist/series`, {
            method: allIn ? 'DELETE' : 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ user_id: currentUser.id, episode_ids: episodeIds }),
          });
          const data = await response.json();
          if (!data.success) throw new Error('refus serveur');
          notify.success(allIn ? 'Série retirée de ma liste' : 'Série ajoutée à ma liste');
        } catch {
          setWatchlist(rollback);
          notify.error('Erreur lors de la modification');
        }
        return;
      }

      /* Un épisode isolé bascule toute sa série. */
      const episode = movies.find((m) => String(m.id) === String(movieId));
      if (episode?.type === 'serie' && episode.tmdbId) {
        return toggleWatchlist(`series_${episode.tmdbId}`);
      }

      const wasIn = watchlist.includes(movieId);
      const rollback = watchlist;
      setWatchlist((prev) => (wasIn ? prev.filter((id) => id !== movieId) : [...prev, movieId]));

      try {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/watchlist/toggle`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ user_id: currentUser.id, movie_id: movieId }),
        });
        const data = await response.json();
        if (!data.success) throw new Error('refus serveur');
        notify.success(data.action === 'removed' ? 'Retiré de ma liste' : 'Ajouté à ma liste');
      } catch {
        setWatchlist(rollback);
        notify.error('Erreur lors de la modification');
      }
    },
    [currentUser, movies, watchlist, notify]
  );

  /** Adaptateur pour les nouveaux composants, qui manipulent des titres. */
  const toggleWatchlistByTitle = useCallback((title) => toggleWatchlist(title.id), [toggleWatchlist]);

  /** Retire un titre de la reprise de lecture (mise à jour optimiste). */
  const forgetProgress = useCallback(
    async (title) => {
      if (!currentUser || !title) return;
      const id = String(title.id);

      const previousList = progressList;
      const previousMap = watchProgress;
      setProgressList((list) => list.filter((entry) => String(entry.movie_id) !== id));
      setWatchProgress((map) => {
        const next = { ...map };
        delete next[id];
        return next;
      });

      try {
        const data = await apiService.removeProgress(currentUser.id, title.id);
        if (!data.success) throw new Error('refus serveur');
        notify.success(`« ${title.title} » retiré de la reprise`);
      } catch {
        setProgressList(previousList);
        setWatchProgress(previousMap);
        notify.error('Impossible de retirer ce titre');
      }
    },
    [currentUser, progressList, watchProgress, notify]
  );

  const reloadWatched = useCallback(async () => {
    if (!currentUser) return;
    try {
      const data = await apiService.getWatchedMovies(currentUser.id);
      if (data.success) setWatchedMovies(data.movie_ids || []);
    } catch (error) {
      console.error('Rechargement des titres vus impossible :', error);
    }
  }, [currentUser]);

  const handleDisableMaintenance = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.NEW_API_BASE}/maintenance`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ enabled: false }),
      });
      const data = await response.json();
      if (data.success) {
        setMaintenanceEnabled(false);
        notify.success('Mode maintenance désactivé');
      }
    } catch {
      notify.error('Erreur lors de la désactivation');
    }
  }, [notify]);

  /* ═══════════════════════ Rendu ══════════════════════════════════ */

  if (sessionUnreachable && !isAuthenticated) {
    return (
      <div className="boot">
        <span className="boot-mark">
          <Film aria-hidden="true" />
        </span>
        <p className="boot-title display-md">Pop Movie</p>
        <p className="boot-note">Serveur injoignable. Votre session est conservée.</p>
        <button className="btn btn-gold" onClick={() => restoreSession()}>
          Réessayer
        </button>
      </div>
    );
  }

  if (booting && !isAuthenticated) {
    return (
      <div className="boot">
        <span className="boot-mark">
          <Film aria-hidden="true" />
        </span>
        <p className="boot-title display-md">Pop Movie</p>
        <p className="boot-note">Préparation de la séance…</p>
        <div className="boot-reel">
          <div className="reel-strip" />
        </div>
      </div>
    );
  }

  const watchlistCount = (() => {
    const series = new Set();
    let films = 0;
    watchlist.forEach((id) => {
      const movie = movies.find((m) => String(m.id) === String(id));
      if (!movie) return;
      if (movie.type === 'serie') series.add(movie.tmdbId);
      else films += 1;
    });
    return films + series.size;
  })();

  return (
    <CatalogProvider
      movies={movies}
      watchlist={watchlist}
      watchedMovies={watchedMovies}
      watchProgress={watchProgress}
      progressList={progressList}
      dislikedMovies={dislikedMovies}
      currentUser={currentUser}
      onOpenTitle={openTitle}
      onToggleWatchlist={toggleWatchlistByTitle}
      onForgetProgress={forgetProgress}
    >
      <PullToRefresh onRefresh={handleRefresh}>
        {maintenanceEnabled && currentUser?.role === 'admin' && (
          <MaintenanceBanner onDisable={handleDisableMaintenance} />
        )}

        <AppRoutes
          isAuthenticated={isAuthenticated}
          currentUser={currentUser}
          maintenanceEnabled={maintenanceEnabled}
          maintenanceMessage={maintenanceMessage}
          onDisableMaintenance={handleDisableMaintenance}
          onLoginSuccess={handleLoginSuccess}
          allMovies={allMovies}
          fetchAllMoviesForAdmin={fetchAllMoviesForAdmin}
          shell={{
            watchlistCount,
            onOpenSettings: () => setShowSettings(true),
            onRequestMovie: () => setShowMovieRequest(true),
            onOpenTheater: () => setShowTheater(true),
            onOpenPlaylists: () => setShowPlaylists(true),
            onRefresh: handleRefresh,
            onOpenAdmin: async () => {
              await fetchAllMoviesForAdmin();
              navigate('/admin');
            },
            onLogout: handleLogout,
          }}
        />
      </PullToRefresh>

      {/* ── Fiche d'un titre ── */}
      {selectedMovie && (
        <TitleSheet
          movie={selectedMovie}
          user={currentUser}
          allMovies={movies}
          watchlist={watchlist}
          onToggleWatchlist={toggleWatchlist}
          onSelectMovie={setSelectedMovie}
          onWatchedStatusChange={reloadWatched}
          onPlayingStatusChange={setIsWatchingVideo}
          onClose={closeTitle}
        />
      )}

      {/* ── Fiche d'une série ── */}
      {selectedSeries && !selectedMovie && (
        <SeriesView
          series={selectedSeries}
          onClose={() => {
            setSelectedSeries(null);
            navigate('/');
          }}
          onSelectEpisode={(episode) => {
            setSelectedMovie(episode);
            navigate(`/film/${episode.id}`);
          }}
          watchlist={watchlist}
          onToggleWatchlist={toggleWatchlist}
        />
      )}

      {/* ── Surcouches ── */}
      {showMovieRequest && (
        <MovieRequestForm user={currentUser} onClose={() => setShowMovieRequest(false)} />
      )}
      {showSettings && (
        <SettingsModal
          user={currentUser}
          onClose={() => setShowSettings(false)}
          onLogout={handleLogout}
        />
      )}
      {showPlaylists && (
        <MyPlaylists
          user={currentUser}
          onClose={() => setShowPlaylists(false)}
          onPlayMovie={(movie) => {
            setShowPlaylists(false);
            openTitle(movie);
          }}
        />
      )}
      {showTheater && (
        <WatchPartyTheater onClose={() => setShowTheater(false)} currentUser={currentUser} />
      )}
      {showChangelog && currentUser && (
        <ChangelogModal user={currentUser} onClose={() => setShowChangelog(false)} />
      )}

      {isAuthenticated && currentUser && (
        <ChatWidget currentUser={currentUser} isWatchingVideo={isWatchingVideo} />
      )}

      {/* ── Succès débloqués ── */}
      <div className="fixed top-0 right-0 z-[100] flex flex-col gap-3 p-4 pointer-events-none">
        <div className="pointer-events-auto">
          {notifications.map((achievement) => (
            <AchievementNotification
              key={achievement.id}
              achievement={achievement}
              onClose={() => removeNotification(achievement.id)}
            />
          ))}
        </div>
      </div>

      <ScrollToTop />
    </CatalogProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <NotificationProvider>
        <AppContent />
        <NotificationContainer />
      </NotificationProvider>
    </ErrorBoundary>
  );
}
